/**
 * Window area distribution across thermal zones.
 *
 * Zones may declare explicit {@link InsolationSurface} windows. The remaining
 * building window area is allocated to zones without own windows using
 * activity weights ({@link WIN_WEIGHTS}) and a max window-to-floor ratio cap
 * ({@link WFR_MAX}), via weighted water-filling in {@link computeWindowShares}.
 */
import { ActivityId } from '@shared/models/activity-class.model';
import { ActivityEnum } from '@shared/models/activities.model';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';
import { EnergyZoneActivity } from './energy-activity.helpers';
import { ActivityInput } from './energy.models';
import { InsolationSurface } from './weather.models';
import { sum } from '../math.helpers';

// Relative likelihood that a zone has windows compared to other zones.
// Used to distribute the building's total window area across zones without own windows.
// TODO Remove ActivityInput and use ActivityType instead
export const WIN_WEIGHTS: Partial<Record<ActivityInput, number>> &
  Record<ActivityType, number> = {
  // --- Industry ---
  [ActivityId.IndustryOffice]: 1.2, // office portion within industry
  [ActivityType.Office]: 1.2,
  [ActivityId.Industry]: 0.2, // production halls
  [ActivityId.Warehouse]: 0.1,
  [ActivityId.Brewery]: 0.2,
  [ActivityId.EmployeeParking]: 0.05,
  [ActivityId.Archive]: 0.05,
  [ActivityId.ConcreteIndustry]: 0.15,

  // --- Gathering ---
  [ActivityId.Gathering]: 0.9, // general
  [ActivityId.Retail]: 1.3, // shop front / display windows
  [ActivityId.ShoppingCenter]: 0.9, // large share inward / indoor environment
  [ActivityId.PublicParking]: 0.05,
  [ActivityId.GymnasticHall]: 0.6,
  [ActivityId.Restaurant]: 1.3,
  [ActivityId.NightClub]: 0.2, // blackout / light control

  // --- Residential ---
  [ActivityType.Residential]: 1.0,
  [ActivityId.Residential]: 1.0,
  [ActivityEnum.Apartments]: 1.0,
  [ActivityId.PrivateHousing]: 1.05, // small extra due to facade-facing rooms
  [ActivityId.ResidentParking]: 0.05,
  [ActivityId.CollectiveHousing]: 0.9,

  // --- Hotel ---
  [ActivityId.Hotel]: 1.0,

  // --- Public buildings ---
  [ActivityId.Public]: 1.0, // fallback when only class is specified
  [ActivityId.School]: 0.9,
  [ActivityType.School]: 0.9,
  [ActivityId.Library]: 1.2,
  [ActivityId.SpecialNeedHousing]: 1.0, // similar to residential
  [ActivityId.Hospital]: 0.8,
  [ActivityId.DetentionCenter]: 0.2, // security

  // --- Fire hazardous ---
  [ActivityId.FireHazardous]: 0.05,

  // --- Common areas ---
  [ActivityId.CommonAreas]: 0.2, // stairwells, corridors, etc.
  [ActivityEnum.Stairwells]: 0.2, // stairwells, corridors, etc.
  [ActivityEnum.BikeRoom]: 0.2, // bike room
  [ActivityEnum.LaundryRoom]: 0.2, // laundry rooms
  [ActivityEnum.Storage]: 0.2, // storage
  [ActivityEnum.BuildingUtilities]: 0.2, // fan room
  [ActivityEnum.Garage]: 0.2, // garage
  [ActivityType.Garage]: 0.2, // garage
  [ActivityEnum.Other]: 0.2, // other
  [ActivityType.Other]: 0.2, // other
  [ActivityType.Unset]: 0.2, // other
  [ActivityType.Industry]: 0.2, // industry
  [ActivityType.Retail]: 1.3,
  [ActivityType.Stairwells]: 0.2, // stairwells, corridors, etc.
  [ActivityType.Innerwalls]: 0.2, // inner wall share / other internal surfaces
  [ActivityType.Storage]: 0.2, // storage
  [ActivityType.Utilities]: 0.2, // fan room
  [ActivityType.Facades]: 0, // not a usable zone; keep at 0 to avoid allocating windows here
  [ActivityType.Installations]: 0, // not a usable zone; keep at 0 to avoid allocating windows here
};

// Upper cap for a reasonable WFR (window area / floor area in zone) in the fallback.
// Prevents e.g. stairwells from getting disproportionate glazing.
export const WFR_MAX: Partial<Record<ActivityInput, number>> = {
  // --- Industry ---
  [ActivityId.IndustryOffice]: 0.45,
  [ActivityType.Office]: 0.45,
  [ActivityId.Industry]: 0.15,
  [ActivityType.Industry]: 0.15,
  [ActivityId.Warehouse]: 0.1,
  [ActivityId.Brewery]: 0.15,
  [ActivityId.EmployeeParking]: 0.05,
  [ActivityId.Archive]: 0.05,
  [ActivityId.ConcreteIndustry]: 0.15,

  // --- Gathering ---
  [ActivityId.Gathering]: 0.35,
  [ActivityId.Retail]: 0.5,
  [ActivityType.Retail]: 0.5,
  [ActivityId.ShoppingCenter]: 0.3,
  [ActivityId.PublicParking]: 0.05,
  [ActivityId.GymnasticHall]: 0.25,
  [ActivityId.Restaurant]: 0.5,
  [ActivityId.NightClub]: 0.15,

  // --- Residential ---
  [ActivityId.Residential]: 0.35,
  [ActivityId.PrivateHousing]: 0.35,
  [ActivityId.ResidentParking]: 0.05,
  [ActivityId.CollectiveHousing]: 0.35,
  [ActivityEnum.Apartments]: 0.35,
  [ActivityType.Residential]: 0.35,

  // --- Hotel ---
  [ActivityId.Hotel]: 0.4,

  // --- Public buildings ---
  [ActivityId.Public]: 0.35,
  [ActivityId.School]: 0.35,
  [ActivityId.Library]: 0.45,
  [ActivityId.SpecialNeedHousing]: 0.35,
  [ActivityId.Hospital]: 0.3,
  [ActivityId.DetentionCenter]: 0.15,

  // --- Fire hazardous ---
  [ActivityId.FireHazardous]: 0.05,

  // --- Common areas ---
  [ActivityId.CommonAreas]: 0.1,
  [ActivityEnum.Stairwells]: 0.1, // stairwells, corridors, etc.
  [ActivityEnum.BikeRoom]: 0.1, // bike room
  [ActivityEnum.LaundryRoom]: 0.1, // laundry rooms
  [ActivityEnum.Storage]: 0.1, // storage
  [ActivityEnum.BuildingUtilities]: 0.1, // fan room
  [ActivityEnum.Garage]: 0.1, // garage
  [ActivityEnum.Other]: 0.1, // other
  [ActivityType.Facades]: 0.1, // not a usable zone; keep at 0 to avoid allocating windows here
  [ActivityType.Installations]: 0.1, // not a usable zone; keep at 0 to avoid allocating windows here
  [ActivityType.Other]: 0.1, // other
  [ActivityType.Unset]: 0.1, // other
};

const DEFAULT_WFR_MAX = 0.35;
const DEFAULT_WIN_WEIGHT = 1.0;
const AREA_EPSILON = 1e-6;

export type WindowShareOptions = {
  /** Relative likelihood per activity that the zone has windows */
  weights?: Partial<Record<ActivityInput, number>>;
  /** Max window-to-floor ratio per activity */
  wfrMax?: Partial<Record<ActivityInput, number>>;
  defaultWeight?: number;
  defaultWfrMax?: number;
};

/**
 * Sum the explicit window area declared on each zone, scaled down
 * proportionally if the explicit windows exceed the building total.
 * @param activities Zones, possibly with explicit windows
 * @param totalWindowArea Building total window area (m²)
 * @returns Explicit window area per zone (m²)
 */
const getExplicitWindowAreas = (
  activities: EnergyZoneActivity[],
  totalWindowArea: number,
): number[] => {
  const explicitAreas = activities.map((activity) =>
    sum(activity.windows ?? [], 'area'),
  );
  const sumExplicit = sum(explicitAreas);
  if (sumExplicit <= totalWindowArea + AREA_EPSILON) return explicitAreas;

  // Guard: scale down proportionally when explicit windows exceed the total
  const scale = totalWindowArea / sumExplicit;
  return explicitAreas.map((area) => area * scale);
};

/**
 * Distribute a free window area over zones using weighted water-filling
 * with a capacity (WFR) cap per zone. Any remainder that cannot fit under
 * the caps is distributed by weight, ignoring caps.
 * @param freeWindowArea Window area to distribute (m²)
 * @param poolIndexes Zone indexes eligible for allocation
 * @param weights Weight per zone index
 * @param caps Max window area per zone index (m²)
 * @returns Allocated window area per zone index (m²)
 */
const allocateWindowAreaByWeight = (
  freeWindowArea: number,
  poolIndexes: number[],
  weights: Map<number, number>,
  caps: Map<number, number>,
): Map<number, number> => {
  const alloc = new Map<number, number>();
  let remaining = freeWindowArea;
  let active = poolIndexes.filter((i) => (weights.get(i) ?? 0) > 0);

  // Water-filling: allocate proportionally, clamp at caps, repeat with the rest
  while (remaining > AREA_EPSILON && active.length > 0) {
    const weightSum = sum(active.map((i) => weights.get(i) ?? 0));
    if (weightSum <= 0) break;

    let used = 0;
    const stillActive: number[] = [];
    for (const i of active) {
      const proposed = (remaining * (weights.get(i) ?? 0)) / weightSum;
      const current = alloc.get(i) ?? 0;
      const cap = caps.get(i) ?? Infinity;
      const add = Math.min(proposed, Math.max(0, cap - current));
      if (add > 0) {
        alloc.set(i, current + add);
        used += add;
      }
      // Still below cap? Keep the zone for the next round
      if ((alloc.get(i) ?? 0) < cap - 1e-9) stillActive.push(i);
    }
    remaining -= used;

    // No zone could take more (all at cap) -> stop
    if (used < 1e-9) break;
    active = stillActive;
  }

  if (remaining > AREA_EPSILON) {
    // Distribute the rest by weight, ignoring caps
    const ranked = active.length ? active : poolIndexes;
    const weightSum = sum(ranked.map((i) => weights.get(i) ?? 0)) || 1;
    for (const i of ranked) {
      const add = remaining * ((weights.get(i) ?? 0) / weightSum);
      alloc.set(i, (alloc.get(i) ?? 0) + add);
    }
  }

  return alloc;
};

/**
 * Compute each zone's share (0-1) of the building's total window area.
 * Zones with explicit windows keep their own area; the remaining window
 * area is distributed over the other zones by activity weight, capped by
 * a max window-to-floor ratio per activity.
 * @param activities Zones to distribute windows over
 * @param buildingWindows The building's window surfaces
 * @param options Weights/caps overrides, see {@link WindowShareOptions}
 * @returns Window area share per zone (same order as activities)
 */
export const computeWindowShares = (
  activities: EnergyZoneActivity[],
  buildingWindows: InsolationSurface[],
  options: WindowShareOptions = {},
): number[] => {
  const {
    weights: weightByActivity = WIN_WEIGHTS,
    wfrMax: wfrMaxByActivity = WFR_MAX,
    defaultWeight = DEFAULT_WIN_WEIGHT,
    defaultWfrMax = DEFAULT_WFR_MAX,
  } = options;

  const totalWindowArea = Math.max(0, sum(buildingWindows, 'area'));
  const zoneCount = activities.length;
  if (totalWindowArea <= 0 || zoneCount === 0) {
    return new Array<number>(zoneCount).fill(0);
  }

  // 1) Explicit windows per zone
  const explicitAreas = getExplicitWindowAreas(activities, totalWindowArea);
  const freeWindowArea = Math.max(0, totalWindowArea - sum(explicitAreas));

  // 2) Candidate zones without own windows
  const poolIndexes = activities
    .map((_, i) => i)
    .filter((i) => explicitAreas[i] === 0);

  // Nothing to distribute: return explicit shares only
  if (freeWindowArea <= AREA_EPSILON || poolIndexes.length === 0) {
    return explicitAreas.map((area) => area / totalWindowArea);
  }

  // 3) Weight and capacity (WFR cap) per zone in the pool
  const weights = new Map<number, number>();
  const caps = new Map<number, number>();
  for (const i of poolIndexes) {
    const activity = activities[i];
    if (!activity) continue;
    const zoneArea = Math.max(0, activity.area || 0);
    weights.set(
      i,
      (weightByActivity[activity.type] ?? defaultWeight) * zoneArea,
    );
    caps.set(i, (wfrMaxByActivity[activity.type] ?? defaultWfrMax) * zoneArea);
  }

  // 4) Water-filling with caps
  const alloc = allocateWindowAreaByWeight(
    freeWindowArea,
    poolIndexes,
    weights,
    caps,
  );

  // 5) Shares: explicit + allocated
  return explicitAreas.map(
    (area, i) => (area + (alloc.get(i) ?? 0)) / totalWindowArea,
  );
};
